import * as THREE from 'three';
import type { HeroState } from '@moba/shared';

/** Segundos que tarda la burbuja en aparecer / desaparecer. */
const POP_IN = 0.22;
const POP_OUT = 0.16;
/** En el último segundo del escudo, la burbuja parpadea para avisar. */
const WARN_TICKS = 30;

const RIM_VERTEX = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vNormal = normalize(normalMatrix * normal);
    vView = normalize(-mv.xyz);
    gl_Position = projectionMatrix * mv;
  }
`;

const RIM_FRAGMENT = /* glsl */ `
  uniform vec3 uColor;
  uniform vec3 uRim;
  uniform float uOpacity;
  uniform float uFlash;
  varying vec3 vNormal;
  varying vec3 vView;
  void main() {
    float fresnel = pow(1.0 - abs(dot(normalize(vNormal), normalize(vView))), 2.2);
    vec3 color = mix(uColor, uRim, fresnel) + uFlash * vec3(1.0, 0.85, 0.5);
    float alpha = (0.12 + fresnel * 0.75 + uFlash * 0.25) * uOpacity;
    gl_FragColor = vec4(color, alpha);
  }
`;

/**
 * Caparazón de púas (W de Bowser): burbuja translúcida con borde brillante y
 * púas de hueso que salen hacia fuera. Se muestra mientras `hero.thornsTicks > 0`.
 */
export class ThornsBubble {
  readonly group = new THREE.Group();
  private readonly shell: THREE.Mesh;
  private readonly shellMaterial: THREE.ShaderMaterial;
  private readonly spikes: THREE.InstancedMesh;
  private readonly spikeMaterial: THREE.MeshStandardMaterial;
  private readonly spikeGeometry: THREE.ConeGeometry;
  private readonly spikeDirs: THREE.Vector3[] = [];
  private readonly radius: number;
  private readonly spikeLength: number;
  private readonly dummy = new THREE.Object3D();
  private presence = 0;
  private flash = 0;
  private lastHp = -1;
  private wasActive = false;

  constructor(heroRadius: number, height: number) {
    this.radius = Math.max(heroRadius * 1.15, height * 0.55);
    this.spikeLength = this.radius * 0.32;
    this.group.position.y = height * 0.48;
    this.group.visible = false;

    this.shellMaterial = new THREE.ShaderMaterial({
      vertexShader: RIM_VERTEX,
      fragmentShader: RIM_FRAGMENT,
      uniforms: {
        uColor: { value: new THREE.Color(0xff8a2a) },
        uRim: { value: new THREE.Color(0xffd27a) },
        uOpacity: { value: 1 },
        uFlash: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.shell = new THREE.Mesh(new THREE.SphereGeometry(this.radius, 40, 24), this.shellMaterial);
    this.group.add(this.shell);

    // púas en los vértices de un icosaedro subdividido, apuntando hacia fuera
    const ico = new THREE.IcosahedronGeometry(1, 1);
    const pos = ico.getAttribute('position');
    const seen = new Set<string>();
    for (let i = 0; i < pos.count; i++) {
      const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
      const key = `${v.x.toFixed(3)},${v.y.toFixed(3)},${v.z.toFixed(3)}`;
      if (seen.has(key) || v.y < -0.75) continue; // sin púas clavadas en el suelo
      seen.add(key);
      this.spikeDirs.push(v);
    }
    ico.dispose();

    this.spikeGeometry = new THREE.ConeGeometry(this.radius * 0.09, 1, 7);
    this.spikeGeometry.translate(0, 0.5, 0); // la base en el origen, la punta en +Y
    this.spikeMaterial = new THREE.MeshStandardMaterial({
      color: 0xf3ead2,
      emissive: 0xff7a1a,
      emissiveIntensity: 0.35,
      roughness: 0.45,
      metalness: 0.1,
      transparent: true,
    });
    this.spikes = new THREE.InstancedMesh(this.spikeGeometry, this.spikeMaterial, this.spikeDirs.length);
    this.group.add(this.spikes);
    this.placeSpikes(1, 0);
  }

  /** Coloca cada púa sobre la burbuja; `extend` (0..1) las saca y `jitter` las hace vibrar. */
  private placeSpikes(extend: number, time: number): void {
    const up = new THREE.Vector3(0, 1, 0);
    this.spikeDirs.forEach((dir, i) => {
      const wobble = 1 + Math.sin(time * 6 + i * 1.7) * 0.06;
      this.dummy.position.copy(dir).multiplyScalar(this.radius * 0.92);
      this.dummy.quaternion.setFromUnitVectors(up, dir);
      this.dummy.scale.set(1, this.spikeLength * extend * wobble, 1);
      this.dummy.updateMatrix();
      this.spikes.setMatrixAt(i, this.dummy.matrix);
    });
    this.spikes.instanceMatrix.needsUpdate = true;
  }

  update(dt: number, hero: HeroState): void {
    const active = hero.alive && hero.thornsTicks > 0;
    // destello y púas más largas cada vez que recibe daño con el escudo puesto
    if (active && this.wasActive && this.lastHp >= 0 && hero.hp < this.lastHp - 0.5) this.flash = 1;
    this.lastHp = hero.hp;
    this.wasActive = active;

    const rate = active ? 1 / POP_IN : -1 / POP_OUT;
    this.presence = THREE.MathUtils.clamp(this.presence + rate * dt, 0, 1);
    this.group.visible = this.presence > 0;
    if (!this.group.visible) return;

    const time = performance.now() * 0.001;
    this.flash = Math.max(0, this.flash - dt * 5);
    // aparece con un pequeño rebote
    const p = this.presence;
    const pop = active ? 1 + Math.sin(p * Math.PI) * 0.12 : 1;
    this.group.scale.setScalar(Math.max(0.001, p * pop));
    this.group.rotation.y += dt * 0.5;

    const warn = active && hero.thornsTicks < WARN_TICKS ? 0.55 + 0.45 * Math.sign(Math.sin(time * 26)) : 1;
    const breathe = 0.9 + Math.sin(time * 5) * 0.1;
    this.shellMaterial.uniforms.uOpacity.value = breathe * warn * p;
    this.shellMaterial.uniforms.uFlash.value = this.flash;
    this.spikeMaterial.opacity = Math.min(1, p * 1.4) * (warn < 1 ? 0.6 + warn * 0.4 : 1);
    this.spikeMaterial.emissiveIntensity = 0.35 + this.flash * 1.4;
    this.placeSpikes(p * (1 + this.flash * 0.35), time);
  }

  dispose(): void {
    this.shell.geometry.dispose();
    this.shellMaterial.dispose();
    this.spikeGeometry.dispose();
    this.spikeMaterial.dispose();
    this.spikes.dispose();
  }
}
